import { useCallback, useState } from "react";
import pick from "lodash/pick";
import { useLocation } from "wouter";
import {
  ErrorResponse,
  useAddLiabilityPaymentMutation,
  useDeleteLiabilityMutation,
  useDeleteLiabilityPaymentMutation,
  useLazyGetAccountDeletionImpactQuery,
  useUpdateLiabilityAccountStatusMutation,
  useUpdateLiabilityPaymentMutation,
  DeletionImpact,
} from "../../../../services";
import { notify } from "../../../../components/Notification";
import { getErrorMessages } from "../../../../services/errors";
import { PAYMENT_FREQUENCY_OPTIONS } from "../../constants";
import {
  LiabilityPayment,
  PaymentObject,
  UpdateLiabilityPayment,
} from "../../panels";
import { formatDate } from "../../../../utils";
import { UseLiabilityActionsParams, UseLiabilityActionsReturn } from "./types";

// RTK Query invalidation refetches detail/history automatically; catch blocks here are only for UI control flow (inline errors, keeping a modal open).
export const useLiabilityActions = (
  params: UseLiabilityActionsParams,
): UseLiabilityActionsReturn => {
  const {
    id,
    liability,
    setActivateModal,
    setIsUpdateOpen,
    setIsDeleteOpen,
    setLiabilityUpdate,
    setDeletePayment,
    setPanel,
    panel,
    deletePayment,
  } = params;

  const [, setLocation] = useLocation();
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);
  const [deleteLiabilityError, setDeleteLiabilityError] = useState<
    string | null
  >(null);
  const [deletePaymentError, setDeletePaymentError] = useState<string | null>(
    null,
  );
  const [deletionImpact, setDeletionImpact] = useState<DeletionImpact | null>(
    null,
  );

  const [updateAccountStatus] = useUpdateLiabilityAccountStatusMutation();
  const [deleteLiabilityTrigger, { isLoading: isDeletingLiability }] =
    useDeleteLiabilityMutation();
  const [deletePaymentTrigger, { isLoading: isDeletingPayment }] =
    useDeleteLiabilityPaymentMutation();
  const [addPayment] = useAddLiabilityPaymentMutation();
  const [updatePayment] = useUpdateLiabilityPaymentMutation();
  const [fetchImpact] = useLazyGetAccountDeletionImpactQuery();

  const resetDeletePaymentError = useCallback(
    () => setDeletePaymentError(null),
    [],
  );

  const fetchDeletionImpact = useCallback(async () => {
    setIsLoadingImpact(true);
    try {
      const impact = await fetchImpact(liability.accountId).unwrap();
      setDeletionImpact(impact);
    } catch {
      // Fall back to the static warning so deletion isn't blocked by a failed impact fetch.
      setDeletionImpact(null);
    } finally {
      setIsLoadingImpact(false);
    }
  }, [fetchImpact, liability.accountId]);

  const handleEdit = useCallback(() => {
    setLiabilityUpdate({
      ...pick(liability, ["name", "institution", "startDate", "endDate"]),
      amount: liability.amountLent.toString(),
      liabilityType: {
        label: liability.liabilityTypeName,
        value: String(liability.liabilityTypeId),
      },
      paymentFrequency:
        PAYMENT_FREQUENCY_OPTIONS.find(
          (option) => option.value === liability.paymentFrequency,
        ) ?? null,
      paymentDueDay: liability.paymentDueDay ?? null,
    });
    setIsUpdateOpen(true);
  }, [liability, setLiabilityUpdate, setIsUpdateOpen]);

  const handleOnStatusChangeClick = useCallback(async () => {
    const message = liability.isActive
      ? "Liability deactivated"
      : "Liability reactivated";
    try {
      await updateAccountStatus({ id, isActive: !liability.isActive }).unwrap();
      notify.success(message);
    } catch {
      // Global middleware shows the toast; just close the modal.
    } finally {
      setActivateModal(false);
    }
  }, [id, liability.isActive, updateAccountStatus, setActivateModal]);

  const handleDelete = useCallback(() => {
    setDeleteLiabilityError(null);
    setIsDeleteOpen(true);
  }, [setIsDeleteOpen]);

  const handleOnDeletePayment = useCallback(async () => {
    setDeletePaymentError(null);
    try {
      await deletePaymentTrigger({ id, paymentId: deletePayment.id }).unwrap();
      notify.success("Liability payment deleted");
      setDeletePayment({ id: 0, amount: 0, isOpen: false });
    } catch (err) {
      // Keep the modal open with an inline error so the user can retry.
      setDeletePaymentError(getErrorMessages(err as ErrorResponse).join(" "));
    }
  }, [id, deletePayment.id, deletePaymentTrigger, setDeletePayment]);

  const handleOnDeleteLiability = useCallback(async () => {
    setDeleteLiabilityError(null);
    try {
      await deleteLiabilityTrigger(id).unwrap();
      notify.success("Liability deleted");
      setLocation("/liabilities");
      setIsDeleteOpen(false);
    } catch (err) {
      // Keep the modal open with an inline error so the user can retry.
      setDeleteLiabilityError(getErrorMessages(err as ErrorResponse).join(" "));
    }
  }, [id, deleteLiabilityTrigger, setLocation, setIsDeleteOpen]);

  const handleSubmit = useCallback(
    async (data: PaymentObject) => {
      try {
        if (panel.action === "edit") {
          const payload: UpdateLiabilityPayment = {
            amount: Number.parseFloat(data.amount),
            principalAmount: Number.parseFloat(data.principalAmount),
            paymentDate: formatDate(data.paymentDate),
            fromAccountId: Number(data.fromAccount!.value),
            note: data.note,
          };
          await updatePayment({ id, paymentId: data.id!, payload }).unwrap();
          notify.success("Liability payment updated");
        } else {
          const payload: LiabilityPayment = {
            amount: Number.parseFloat(data.amount),
            principalAmount: Number.parseFloat(data.principalAmount),
            paymentDate: formatDate(data.paymentDate),
            fromAccountId: Number(data.fromAccount!.value),
            note: data.note,
          };
          await addPayment({ id, payload }).unwrap();
          notify.success("Liability payment created!");
        }
        setPanel({ ...panel, isOpen: false, payment: undefined });
      } catch {
        // Keep the panel open on failure; the global middleware shows the toast.
      }
    },
    [id, panel, addPayment, updatePayment, setPanel],
  );

  return {
    handleEdit,
    handleOnStatusChangeClick,
    handleDelete,
    handleOnDeletePayment,
    handleOnDeleteLiability,
    handleSubmit,
    isLoadingImpact,
    isDeletingLiability,
    deleteLiabilityError,
    isDeletingPayment,
    deletePaymentError,
    deletionImpact,
    fetchDeletionImpact,
    resetDeletePaymentError,
  };
};
